/**
 * 通用功能布局 — 复用 MainLayout 的侧边栏样式
 */
import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, LogOut, ArrowLeftRight, ChevronLeft, ChevronRight, Menu, X, Bell, Heart, MessageSquare, Check, Loader2, Star } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { useToast } from '@/components/Toast';
import mainStyles from './MainLayout.module.css';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  permission?: string;
  roles?: string[];
}

interface Props {
  title: string;
  functionKey: string;
  navItems: NavItem[];
}

export default function FunctionLayout({ title, functionKey, navItems }: Props) {
  const { user, logout, hasPermission, currentFunctionRole, functions } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const effectiveRole = currentFunctionRole || user?.role_name || '';
  const hideSidebar = effectiveRole === '客户' || effectiveRole === '供应商';
  const hideSwitch = hideSidebar || (localStorage.getItem('currentFunctionRole') || localStorage.getItem('role_name') || '') === '客户' || (localStorage.getItem('currentFunctionRole') || localStorage.getItem('role_name') || '') === '供应商';

  const [showMessages, setShowMessages] = useState(false);
  const [messages, setMessages] = useState<any[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgFilter, setMsgFilter] = useState('all');
  const [readMsgIds, setReadMsgIds] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('msg_read')||'[]')); } catch { return new Set(); }
  });
  const msgRef = useRef<HTMLDivElement>(null);
  const unreadCount = messages.filter((m:any) => !readMsgIds.has(m.type+'-'+m.id)).length;

  const fetchMessages = async () => {
    if (functionKey !== 'share') return;
    setMsgLoading(true);
    try { const res = await api.request<any[]>('/promotions/messages'); setMessages(res.data || []); }
    catch { setMessages([]); }
    finally { setMsgLoading(false); }
  };
  useEffect(() => { fetchMessages(); }, []);

  const closeMessages = () => {
    const ids = messages.map((m:any) => m.type+'-'+m.id);
    if (ids.length > 0) {
      setReadMsgIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.add(id));
        localStorage.setItem('msg_read', JSON.stringify([...next]));
        return next;
      });
    }
    setShowMessages(false);
  };

  const [pendingCount, setPendingCount] = useState(0);
  const [msgUnrepliedCount, setMsgUnrepliedCount] = useState(0);
  const [commentPendingCount, setCommentPendingCount] = useState(0);
  // 学习考试：我的任务未完成数（学习任务 + 考试任务，状态为待开始/进行中）
  const [myTaskCount, setMyTaskCount] = useState(0);
  useEffect(() => { if (functionKey === 'share') { fetchPendingCount(); fetchMsgCount(); fetchCommentPendingCount(); } }, []);
  useEffect(() => { if (functionKey === 'exam') { fetchMyTaskCount(); } }, []);
  // 监听子页面触发的刷新事件（审核/回复完成后即时更新红标）
  useEffect(() => {
    const refresh = () => { fetchPendingCount(); fetchMsgCount(); fetchCommentPendingCount(); fetchMyTaskCount(); };
    window.addEventListener('refresh-sidebar-counts', refresh);
    return () => window.removeEventListener('refresh-sidebar-counts', refresh);
  }, []);
  const fetchPendingCount = async () => {
    const role = currentFunctionRole || user?.role_name || '';
    if (!['采购','研发'].includes(role)) return;
    try { const res = await api.request<{count:number}>('/promotions/approvals/count'); setPendingCount(res.data?.count || 0); }
    catch { setPendingCount(0); }
  };
  const fetchMsgCount = async () => {
    const role = currentFunctionRole || user?.role_name || '';
    if (role !== '销售') return;
    try { const res = await api.request<any>('/promotions/message-board'); setMsgUnrepliedCount((res.data||[]).filter((m:any)=>m.status===0).length); }
    catch { setMsgUnrepliedCount(0); }
  };
  const fetchCommentPendingCount = async () => {
    const role = currentFunctionRole || user?.role_name || '';
    if (role !== '销售') return;
    try { const res = await api.request<any>('/promotions/comments/pending'); setCommentPendingCount((res.data||[]).length); }
    catch { setCommentPendingCount(0); }
  };
  const fetchMyTaskCount = async () => {
    if (!user?.user_id) return;
    try {
      const [examRes, learningRes] = await Promise.all([
        api.getMyExamTasks({ student_id: user.user_id, page: 1, page_size: 100 }),
        api.getMyLearningTasks({ student_id: user.user_id, page: 1, page_size: 100 }),
      ]);
      const isPending = (t: { status: string }) => t.status === 'pending' || t.status === 'ongoing';
      const count =
        (examRes.data || []).filter(isPending).length +
        (learningRes.data || []).filter(isPending).length;
      setMyTaskCount(count);
    } catch { setMyTaskCount(0); }
  };

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (msgRef.current && !msgRef.current.contains(e.target as Node)) setShowMessages(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredNav = navItems.filter(item => {
    if (!item.permission || !hasPermission(item.permission)) return false;
    if (item.roles && !item.roles.includes(currentFunctionRole || '')) return false;
    return true;
  });

  // 切换功能：仅一个可用功能模块时提示，不跳转选择页
  const handleSwitchFunction = () => {
    const uniqueFuncs = new Set(functions.map(f => f.function));
    if (uniqueFuncs.size <= 1) {
      showToast('warning', '您只有一个可用的功能模块，无需切换');
      return;
    }
    navigate('/select-function');
  };

  return (
    <div className={mainStyles.container}>
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div className={mainStyles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={() => setIsMobileOpen(false)} />
        )}
      </AnimatePresence>

      {!hideSidebar && (
        <aside className={`${mainStyles.sidebar} ${isCollapsed ? mainStyles.collapsed : ''} ${isMobileOpen ? mainStyles.mobileOpen : ''}`}>
          <div className={mainStyles.logoSection}>
            <div className={mainStyles.logo}><BookOpen size={24} strokeWidth={1.5} /></div>
            {!isCollapsed && (
              <motion.div className={mainStyles.logoText} initial={{opacity:0,x:-10}} animate={{opacity:1,x:0}} transition={{delay:0.1}}>
                <span className={mainStyles.logoTitle}>{title}</span>
                <span className={mainStyles.logoSubtitle}>角色: {currentFunctionRole || user?.role_name}</span>
              </motion.div>
            )}
          </div>
          <nav className={mainStyles.nav}>
            {filteredNav.map((item, index) => (
              <NavLink key={item.path} to={item.path} end
                className={({isActive}) => `${mainStyles.navItem} ${isActive ? mainStyles.active : ''}`}
                onClick={() => setIsMobileOpen(false)}>
                <motion.div className={mainStyles.navItemContent} initial={{opacity:0,x:-20}} animate={{opacity:1,x:0}} transition={{delay:index*0.05}}>
                  <span className={mainStyles.navIcon}>{item.icon}</span>
                  {!isCollapsed && <motion.span className={mainStyles.navLabel} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:index*0.05+0.1}}>
                  {item.label}
                  {item.label === '视频审核' && pendingCount > 0 && (
                    <span style={{marginLeft:6,background:'#dc2626',color:'#fff',fontSize:10,minWidth:18,height:18,borderRadius:9,display:'inline-flex',alignItems:'center',justifyContent:'center',fontWeight:600,verticalAlign:'middle'}}>
                      {pendingCount > 99 ? '99+' : pendingCount}
                    </span>
                  )}
                  {item.label === '评论审核' && commentPendingCount > 0 && (
                    <span style={{marginLeft:6,background:'#ef4444',color:'#fff',fontSize:10,minWidth:18,height:18,borderRadius:9,display:'inline-flex',alignItems:'center',justifyContent:'center',fontWeight:600,verticalAlign:'middle'}}>
                      {commentPendingCount > 99 ? '99+' : commentPendingCount}
                    </span>
                  )}
                  {item.label === '留言管理' && msgUnrepliedCount > 0 && (
                    <span style={{marginLeft:6,background:'#ef4444',color:'#fff',fontSize:10,minWidth:18,height:18,borderRadius:9,display:'inline-flex',alignItems:'center',justifyContent:'center',fontWeight:600,verticalAlign:'middle'}}>
                      {msgUnrepliedCount > 99 ? '99+' : msgUnrepliedCount}
                    </span>
                  )}
                  {item.label === '我的任务' && myTaskCount > 0 && (
                    <span style={{marginLeft:6,background:'#ef4444',color:'#fff',fontSize:10,minWidth:18,height:18,borderRadius:9,display:'inline-flex',alignItems:'center',justifyContent:'center',fontWeight:600,verticalAlign:'middle'}}>
                      {myTaskCount > 99 ? '99+' : myTaskCount}
                    </span>
                  )}
                </motion.span>}
                </motion.div>
                {!isCollapsed && <div className={mainStyles.navIndicator} />}
              </NavLink>
            ))}
          </nav>
          <button className={mainStyles.collapseButton} onClick={() => setIsCollapsed(!isCollapsed)} title={isCollapsed ? '展开侧边栏' : '收起侧边栏'}>
            {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </aside>
      )}

      <main className={mainStyles.main} style={hideSidebar ? {marginLeft:0,width:'100%'} : undefined}>
        {!hideSidebar && (
          <div className={mainStyles.mobileHeader}>
            <button className={mainStyles.mobileMenuButton} onClick={() => setIsMobileOpen(true)}>
              <Menu size={20} />
            </button>
            <span className={mainStyles.mobileTitle}>{title}</span>
            <div style={{display:'flex',alignItems:'center',gap:8,overflow:'hidden',marginLeft:'auto'}}>
              <span style={{fontSize:'.8125rem',fontWeight:500,color:'var(--ink-700)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:100}}>{user?.user_name}</span>
              <button onClick={handleSwitchFunction} title="切换功能" style={{background:'none',border:'none',cursor:'pointer',color:'var(--ink-400)',padding:4,display: (currentFunctionRole || user?.role_name) === '客户' || (currentFunctionRole || user?.role_name) === '供应商' ? 'none' : undefined}}><ArrowLeftRight size={16} strokeWidth={1.5} /></button>
              <button onClick={logout} style={{background:'none',border:'none',cursor:'pointer',color:'var(--ink-400)',padding:4}}><LogOut size={16} /></button>
            </div>
          </div>
        )}

        {/* 顶部栏 — 移动端 mobileHeader 显示时隐藏 */}
        <div className={`${mainStyles.topBar} ${mainStyles.topBarRight} ${!hideSidebar ? mainStyles.hideOnMobile : ''}`} style={{
          display:'flex', alignItems:'center', gap:16,
          padding:'10px 16px', background:'#fff',
          position:'sticky', top:0, zIndex:91,
          boxShadow:'0 1px 4px rgba(0,0,0,.05)',
        }}>
          {/* 左侧：用户信息 */}
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'4px 0',cursor:'default',overflow:'hidden',maxWidth:'60%'}}>
            <div className={mainStyles.avatar} style={{width:36,height:36,fontSize:'.875rem',fontWeight:700,boxShadow:'0 2px 8px rgba(0,0,0,.08)',flexShrink:0}}>{user?.user_name?.charAt(0) || 'U'}</div>
            <div style={{display:'flex',flexDirection:'column',lineHeight:1.2,overflow:'hidden'}}>
              <span style={{fontSize:'.9375rem',fontWeight:600,color:'var(--ink-700)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{user?.user_name}</span>
              <span style={{fontSize:'.75rem',color:'var(--ink-400)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{currentFunctionRole || user?.role_name}</span>
            </div>
          </div>
          {/* 右侧：操作按钮 */}
          <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
          {functionKey === 'share' && (
            <div style={{position:'relative'}} ref={msgRef}>
              <button onClick={() => { if (showMessages) closeMessages(); else { setShowMessages(true); fetchMessages(); } }} title="消息"
                style={{
                  background:'none',border:'none',cursor:'pointer',padding:8,position:'relative',
                  color:'var(--ink-400)',borderRadius:10,
                }}
              >
                <Bell size={20} strokeWidth={1.5} />
                {unreadCount > 0 && (
                  <span style={{position:'absolute',top:2,right:2,background:'#dc2626',color:'#fff',fontSize:10,minWidth:18,height:18,borderRadius:9,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:600}}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {showMessages && <>
                <div onClick={closeMessages} style={{position:'fixed',inset:0,zIndex:999}} />
                <div style={{position:'absolute',top:'100%',right:0,width:360,maxHeight:440,overflow:'auto',background:'#fff',borderRadius:14,boxShadow:'0 16px 48px rgba(0,0,0,.12)',border:'1px solid var(--ink-100)',zIndex:1000,marginTop:4}}>
                  <div style={{padding:'14px 20px',fontWeight:600,fontSize:'.875rem',borderBottom:'1px solid var(--ink-100)',display:'flex',justifyContent:'space-between',position:'sticky',top:0,background:'#fff'}}>
                    <span>消息中心</span>
                    <button onClick={fetchMessages} style={{background:'none',border:'none',cursor:'pointer',fontSize:'.75rem',color:'var(--ink-400)'}}>刷新</button>
                  </div>
                  <div style={{padding:'6px 16px',display:'flex',gap:6,borderBottom:'1px solid var(--ink-100)'}}>
                    {[{key:'all',label:'全部'},{key:'like',label:'互动'},{key:'comment',label:'评论'},{key:'approval',label:'审核'}].map(cat => {
                      const count = cat.key==='all' ? messages.filter((m:any)=>!readMsgIds.has(m.type+'-'+m.id)).length
                        : cat.key==='like' ? messages.filter((m:any)=>(m.type==='like'||m.type==='fav')&&!readMsgIds.has(m.type+'-'+m.id)).length
                        : messages.filter((m:any)=>m.type===cat.key&&!readMsgIds.has(m.type+'-'+m.id)).length;
                      return (
                        <button key={cat.key} onClick={()=>setMsgFilter(cat.key)} style={{
                          padding:'4px 12px',borderRadius:16,border:'none',cursor:'pointer',fontSize:'.75rem',
                          background:msgFilter===cat.key?'var(--cinnabar-500)':'#f3f4f6',
                          color:msgFilter===cat.key?'#fff':'var(--ink-500)',
                          fontWeight:msgFilter===cat.key?600:400,
                          display:'flex',alignItems:'center',gap:4,
                        }}>
                          {cat.label}
                          {count>0&&<span style={{background:msgFilter===cat.key?'rgba(255,255,255,.3)':'#dc2626',color:msgFilter===cat.key?'#fff':'#fff',fontSize:10,minWidth:16,height:16,borderRadius:8,display:'inline-flex',alignItems:'center',justifyContent:'center'}}>{count>99?'99+':count}</span>}
                        </button>
                      );
                    })}
                  </div>
                  {msgLoading ? (
                    <div style={{padding:30,textAlign:'center',color:'var(--ink-300)',fontSize:'.8125rem'}}><Loader2 size={18} className={mainStyles.spin} /> 加载中...</div>
                  ) : (() => {
                    const filtered = msgFilter==='all' ? messages : msgFilter==='like' ? messages.filter((m:any)=>m.type==='like'||m.type==='fav') : messages.filter((m:any)=>m.type===msgFilter);
                    return filtered.length === 0 ? (
                    <div style={{padding:40,textAlign:'center',color:'var(--ink-300)',fontSize:'.8125rem'}}>暂无消息</div>
                  ) : (
                    filtered.slice(0, 30).map((m: any) => {
                      const umid = m.type+'-'+m.id;
                      const isUnread = !readMsgIds.has(umid);
                      return (
                      <div key={umid} style={{padding:'12px 20px',borderBottom:'1px solid #f3f4f6',fontSize:'.8125rem',display:'flex',gap:10,alignItems:'flex-start',background:isUnread?'#dbeafe':'#f3f4f6',borderLeft:isUnread?'3px solid #3b82f6':'3px solid transparent'}}>
                        <div style={{width:28,height:28,borderRadius:'50%',background:m.type==='like'?'#fef2f2':m.type==='fav'?'#fefce8':m.type==='comment'?'#eff6ff':m.type==='approval'?m.action==='驳回'?'#fee2e2':'#dcfce7':'#f5f5f5',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                          {m.type==='like' && <Heart size={12} style={{color:'#dc2626'}}/>}
                          {m.type==='fav' && <Star size={12} style={{color:'#eab308'}}/>}
                          {m.type==='comment' && <MessageSquare size={12} style={{color:'#2563eb'}}/>}
                          {m.type==='approval' && (m.action==='驳回' ? <X size={12} style={{color:'#dc2626'}}/> : <Check size={12} style={{color:'#16a34a'}}/>)}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          {m.type==='like' && <div style={{color:'var(--ink-600)'}}><strong>{m.user_name}</strong> 赞了 <strong>{m.title}</strong></div>}
                          {m.type==='fav' && <div style={{color:'var(--ink-600)'}}><strong>{m.user_name}</strong> 收藏了 <strong>{m.title}</strong></div>}
                          {m.type==='comment' && <div style={{color:'var(--ink-600)'}}><strong>{m.user_name}</strong> 评论了 <strong>{m.title}</strong></div>}
                          {m.type==='approval' && <div style={{color:'var(--ink-600)'}}><strong>{m.role}</strong> {m.action==='通过'?'通过了':'驳回了'} <strong>{m.title}</strong></div>}
                          {(m.type==='comment'||(m.type==='approval'&&m.comment)) && <div style={{color:'var(--ink-400)',marginTop:3,fontSize:'.75rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{m.content||m.comment}</div>}
                          <div style={{display:'flex',alignItems:'center',gap:8,marginTop:4}}>
                            <span style={{color:'var(--ink-300)',fontSize:'.6875rem'}}>{(()=>{const d=new Date(m.create_time);const df=Date.now()-d.getTime();if(df<6e4)return'刚刚';if(df<36e5)return Math.floor(df/6e4)+'分钟前';if(df<864e5)return Math.floor(df/36e5)+'小时前';return d.toLocaleDateString('zh-CN')})()}</span>
                            {isUnread && <span style={{fontSize:'.625rem',color:'#3b82f6',background:'#dbeafe',padding:'1px 6px',borderRadius:3,fontWeight:500}}>未读</span>}
                          </div>
                        </div>
                      </div>
                    );})
                  );})()}
                </div>
              </>}
            </div>
          )}
          <button onClick={handleSwitchFunction} title="切换功能"
            style={{
              background:'none',border:'none',cursor:'pointer',color:'var(--ink-400)',padding:8,borderRadius:10,
              display: (currentFunctionRole || user?.role_name) === '客户' || (currentFunctionRole || user?.role_name) === '供应商' ? 'none' : undefined
            }}
          >
            <ArrowLeftRight size={20} strokeWidth={1.5} />
          </button>
          <button onClick={logout} title="退出登录"
            style={{
              background:'none',border:'none',cursor:'pointer',color:'var(--ink-400)',padding:8,borderRadius:10,
            }}
          >
            <LogOut size={20} strokeWidth={1.5} />
          </button>
          </div>
        </div>

        <div className={mainStyles.content} style={hideSidebar ? {maxWidth:'100%',padding:'12px'} : undefined}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
